import React, { useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { inputStyle, bigBtn, smallBtn, formOverlay, formContainer, card, fieldRow, fieldLabel, inlineInput, tableStyle, thStyle, trStyle } from '../lib/styles';

const emptyAccounting = {
  entry_type:"INVOICE", load_id:"", driver_id:"",
  amount:0, date:"", status:"PENDING",
  reference_number:"", payment_method:"", notes:"",
};

interface AccountingProps {
  data: any[];
  selectedLoad: any;
  setSelectedLoad: (v: any) => void;
  companyId: string | null;
  newAccounting: typeof emptyAccounting;
  setNewAccounting: (v: typeof emptyAccounting) => void;
  showEntryForm: boolean;
  setShowEntryForm: (v: boolean) => void;
  fetchRoomData: () => void;
}

export default function Accounting({ data, selectedLoad, setSelectedLoad, companyId, newAccounting, setNewAccounting, showEntryForm, setShowEntryForm, fetchRoomData }: AccountingProps) {
  const [editDraft, setEditDraft] = useState<any>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editSaved, setEditSaved] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const openDrillDown = (item: any) => {
    setSelectedLoad(item);
    setEditDraft({ ...item });
    setEditSaved(false);
  };

  const saveEditDraft = async () => {
    if (!editDraft?.id) return;
    setEditSaving(true);
    const payload = { ...editDraft };
    delete payload.id; delete payload.company_id; delete payload.created_at;
    const { data: updated, error } = await (supabase as any).from('accounting').update(payload).eq('id', editDraft.id).select('*').single();
    if (error) { alert(`Save failed: ${error.message}`); setEditSaving(false); return; }
    setSelectedLoad(updated);
    setEditDraft({ ...updated });
    fetchRoomData();
    setEditSaving(false);
    setEditSaved(true);
    setTimeout(() => setEditSaved(false), 2500);
  };

  const handleSave = async () => {
    if (!companyId) { alert("Company not loaded yet."); return; }
    if (!newAccounting.amount) { alert("Amount is required."); return; }
    const payload = { company_id:companyId, ...newAccounting, load_id:newAccounting.load_id||null, driver_id:newAccounting.driver_id||null };
    const { error } = await supabase.from("accounting").insert(payload);
    if (error) { alert(`Accounting insert failed: ${error.message}`); return; }
    setShowEntryForm(false);
    setNewAccounting(emptyAccounting);
    fetchRoomData();
  };

  const totalRevenue = data.reduce((s:number,i:any)=>s+Number(i.amount||0),0);
  const paid = data.filter((i:any)=>i.status==='PAID');
  const pending = data.filter((i:any)=>i.status==='PENDING'||i.status==='INVOICED');
  const overdue = data.filter((i:any)=>i.status==='OVERDUE');

  return (
    <>
      {/* ── CREATE ENTRY FORM ── */}
      {showEntryForm && (
        <div style={formOverlay}>
          <div style={{ ...formContainer, width:"580px", maxHeight:"90vh", overflowY:"auto" }}>
            <h3 style={{ color:"#32CD32", marginTop:0 }}>NEW ACCOUNTING ENTRY</h3>
            <select style={inputStyle as any} value={newAccounting.entry_type} onChange={e=>setNewAccounting({...newAccounting,entry_type:e.target.value})}>
              {["INVOICE","DRIVER SETTLEMENT","FUEL EXPENSE","MAINTENANCE","INSURANCE","OTHER"].map(t=><option key={t} value={t}>{t}</option>)}
            </select>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" }}>
              <input type="number" placeholder="Amount ($) *" style={inputStyle} value={newAccounting.amount||""} onChange={e=>setNewAccounting({...newAccounting,amount:Number(e.target.value)})} />
              <input type="date" style={inputStyle} value={newAccounting.date} onChange={e=>setNewAccounting({...newAccounting,date:e.target.value})} />
            </div>
            <select style={inputStyle as any} value={newAccounting.status} onChange={e=>setNewAccounting({...newAccounting,status:e.target.value})}>
              {["PENDING","PAID","OVERDUE","VOID"].map(s=><option key={s} value={s}>{s}</option>)}
            </select>
            <input type="text" placeholder="Reference Number" style={inputStyle} value={newAccounting.reference_number} onChange={e=>setNewAccounting({...newAccounting,reference_number:e.target.value})} />
            <input type="text" placeholder="Payment Method" style={inputStyle} value={newAccounting.payment_method} onChange={e=>setNewAccounting({...newAccounting,payment_method:e.target.value})} />
            <textarea placeholder="Notes..." style={{...inputStyle,resize:"vertical"} as any} rows={2} value={newAccounting.notes} onChange={e=>setNewAccounting({...newAccounting,notes:e.target.value})} />
            <div style={{ display:"flex", gap:"10px", marginTop:"10px" }}>
              <button onClick={handleSave} style={bigBtn}>SAVE TO SYSTEM</button>
              <button onClick={()=>setShowEntryForm(false)} style={{...bigBtn,background:"#333",color:"#fff"}}>CANCEL</button>
            </div>
          </div>
        </div>
      )}

      {/* ── ACCOUNTING LIST ── */}
      {!selectedLoad && (
        <div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:"12px", marginBottom:"24px" }}>
            {[
              {label:"TOTAL ENTRIES",value:data.length,color:"#ccc"},
              {label:"TOTAL AMOUNT",value:`$${totalRevenue.toLocaleString()}`,color:"#32CD32"},
              {label:"PAID",value:paid.length,color:"#32CD32"},
              {label:"PENDING / OVERDUE",value:`${pending.length} / ${overdue.length}`,color:overdue.length>0?"#ff4444":"#FFD700"},
            ].map((k,i)=>(
              <div key={i} style={{ border:"1px solid #1a1a1a", background:"#050505", padding:"14px" }}>
                <div style={{ color:"#777", fontSize:"9px", letterSpacing:"1.5px", marginBottom:"6px" }}>{k.label}</div>
                <div style={{ color:k.color, fontSize:"20px", fontWeight:900 }}>{k.value}</div>
              </div>
            ))}
          </div>
          {overdue.length > 0 && (
            <div style={{ marginBottom:"16px", padding:"12px 16px", background:"rgba(255,68,68,0.08)", border:"1px solid #ff4444" }}>
              <div style={{ color:"#ff4444", fontSize:"11px", fontWeight:700, letterSpacing:"2px", marginBottom:"8px" }}>OVERDUE INVOICES ({overdue.length})</div>
              {overdue.map((item:any,idx:number)=>(
                <div key={idx} style={{ display:"flex", gap:"12px", padding:"6px 0", borderBottom:"1px solid #1a1a1a", fontSize:"12px" }}>
                  <span style={{ color:"#ff4444", fontWeight:700, width:"120px" }}>{item.reference_number||"---"}</span>
                  <span style={{ color:"#fff", flex:1 }}>{item.notes||item.entry_type||"---"}</span>
                  <span style={{ color:"#ff4444", fontWeight:700 }}>${Number(item.amount||0).toFixed(2)}</span>
                  <button onClick={()=>openDrillDown(item)} style={smallBtn}>EDIT</button>
                </div>
              ))}
            </div>
          )}
          {(()=>{
            const fi: React.CSSProperties = { background:"#0a0a0a", border:"1px solid #2a2a2a", color:"#fff", padding:"6px 10px", fontFamily:"monospace", fontSize:"11px" };
            const q = search.toLowerCase();
            const rows = data.filter((item:any) => {
              if (q && !((item.reference_number||'').toLowerCase().includes(q) || (item.load_id||'').toLowerCase().includes(q) || (item.notes||'').toLowerCase().includes(q))) return false;
              if (typeFilter !== 'ALL' && (item.entry_type||'').toUpperCase() !== typeFilter) return false;
              if (statusFilter !== 'ALL' && (item.status||'').toUpperCase() !== statusFilter) return false;
              return true;
            });
            const amountColor = (t:string) => ['INVOICE','PAYMENT'].includes(t)?'#32CD32':'#ff4444';
            return (
            <>
              <div style={{ display:"flex", gap:"10px", marginBottom:"14px", alignItems:"center", flexWrap:"wrap" }}>
                <input placeholder="Search ref # or notes..." value={search} onChange={e=>setSearch(e.target.value)} style={{ ...fi, width:"200px" }} />
                <select value={typeFilter} onChange={e=>setTypeFilter(e.target.value)} style={fi as any}>
                  {['ALL','INVOICE','PAYMENT','EXPENSE','FUEL','DRIVER PAY','OTHER'].map(t=><option key={t} value={t}>{t==='ALL'?'ALL TYPES':t}</option>)}
                </select>
                <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} style={fi as any}>
                  {['ALL','PENDING','INVOICED','PAID','OVERDUE','VOID'].map(s=><option key={s} value={s}>{s==='ALL'?'ALL STATUSES':s}</option>)}
                </select>
                <span style={{ color:"#555", fontSize:"11px", marginLeft:"auto" }}>{rows.length} entr{rows.length!==1?'ies':'y'}</span>
              </div>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>TYPE</th><th style={thStyle}>REF #</th><th style={thStyle}>AMOUNT</th>
                    <th style={thStyle}>DATE</th><th style={thStyle}>STATUS</th><th style={thStyle}>NOTES</th><th style={thStyle}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((item:any,idx:number)=>(
                    <tr key={idx} style={trStyle}>
                      <td style={{ padding:"10px 12px", color:amountColor(item.entry_type||''), fontWeight:700 }}>{item.entry_type||"---"}</td>
                      <td style={{ padding:"10px 12px", color:"#aaa" }}>{item.reference_number||"---"}</td>
                      <td style={{ padding:"10px 12px", color:amountColor(item.entry_type||''), fontWeight:700 }}>${Number(item.amount||0).toFixed(2)}</td>
                      <td style={{ padding:"10px 12px", color:"#aaa" }}>{item.date||"---"}</td>
                      <td style={{ padding:"10px 12px" }}><span style={{ color:item.status==="PAID"?"#32CD32":item.status==="OVERDUE"?"#ff4444":"#ffaa00", fontSize:"10px", border:"1px solid currentColor", padding:"2px 6px" }}>{item.status||"---"}</span></td>
                      <td style={{ padding:"10px 12px", color:"#777", fontSize:"11px" }}>{item.notes||"---"}</td>
                      <td style={{ padding:"10px 12px" }}><button onClick={()=>openDrillDown(item)} style={smallBtn}>EDIT</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
            );
          })()}
        </div>
      )}

      {/* ── ACCOUNTING DRILL DOWN ── */}
      {selectedLoad && (
        <div style={card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px" }}>
            <button onClick={()=>setSelectedLoad(null)} style={{ color:"#32CD32", background:"none", border:"none", cursor:"pointer", fontSize:"13px" }}>{"← BACK TO ACCOUNTING"}</button>
            <div style={{ display:"flex", gap:"8px", alignItems:"center" }}>
              {editSaved && <span style={{ color:"#32CD32", fontSize:"11px" }}>✓ SAVED</span>}
              <button onClick={saveEditDraft} disabled={editSaving} style={{ ...smallBtn, background:editSaving?"#1a1a1a":"#32CD32", color:editSaving?"#555":"#000" }}>{editSaving?"SAVING...":"SAVE CHANGES"}</button>
            </div>
          </div>
          <h3 style={{ marginTop:0 }}>ACCOUNTING ENTRY</h3>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"16px" }}>
            <div style={{ border:"1px solid #1e1e1e", padding:"16px", background:"#0d0d0d" }}>
              <div style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"2px", fontWeight:700, borderBottom:"1px solid #1e1e1e", paddingBottom:"8px", marginBottom:"12px" }}>ENTRY DETAILS</div>
              <div style={fieldRow}><span style={fieldLabel}>TYPE</span>
                <select style={inlineInput as any} value={editDraft?.entry_type||"INVOICE"} onChange={e=>setEditDraft({...editDraft,entry_type:e.target.value})}>
                  {["INVOICE","PAYMENT","EXPENSE","FUEL","DRIVER PAY","OTHER"].map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={fieldRow}><span style={fieldLabel}>AMOUNT ($)</span><input type="number" style={inlineInput} value={editDraft?.amount||""} onChange={e=>setEditDraft({...editDraft,amount:Number(e.target.value)})} /></div>
              <div style={fieldRow}><span style={fieldLabel}>DATE</span><input type="date" style={inlineInput} value={editDraft?.date||""} onChange={e=>setEditDraft({...editDraft,date:e.target.value})} /></div>
              <div style={fieldRow}><span style={fieldLabel}>REF #</span><input style={inlineInput} value={editDraft?.reference_number||""} onChange={e=>setEditDraft({...editDraft,reference_number:e.target.value})} /></div>
            </div>
            <div style={{ border:"1px solid #1e1e1e", padding:"16px", background:"#0d0d0d" }}>
              <div style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"2px", fontWeight:700, borderBottom:"1px solid #1e1e1e", paddingBottom:"8px", marginBottom:"12px" }}>STATUS & NOTES</div>
              <div style={fieldRow}><span style={fieldLabel}>STATUS</span>
                <select style={inlineInput as any} value={editDraft?.status||"PENDING"} onChange={e=>setEditDraft({...editDraft,status:e.target.value})}>
                  {["PENDING","INVOICED","PAID","OVERDUE","VOID"].map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={fieldRow}><span style={fieldLabel}>DUE DATE</span><input type="date" style={inlineInput} value={editDraft?.due_date||""} onChange={e=>setEditDraft({...editDraft,due_date:e.target.value})} /></div>
              <div style={{ marginTop:"12px" }}>
                <div style={{ color:"#555", fontSize:"10px", marginBottom:"6px" }}>NOTES</div>
                <textarea style={{ ...inlineInput, resize:"vertical", minHeight:"80px" } as any} value={editDraft?.notes||""} onChange={e=>setEditDraft({...editDraft,notes:e.target.value})} placeholder="Invoice notes, payment details, load reference..." />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
